/**
 * 根节点 zoom：抵消 Windows 系统显示缩放（120%/125%/150% 等），
 * 让一屏内容量接近「系统 100%」时的可视密度。
 *
 * 公式：zoom ≈ 1 / devicePixelRatio（上限 1，下限 MIN_ZOOM）
 * iOS / iPadOS / macOS：不启用（Retina 正常显示即为正确，勿按 DPR 回缩）
 *
 * 环境变量：
 * - VITE_IPC_ZOOM_ENABLED=false 关闭
 * - VITE_IPC_ZOOM_FORCE=0.8 强制某一档（调试用，仍受平台限制）
 */

/** 过小则热区难点，150% 时 1/1.5≈0.667 */
export const MIN_ZOOM = 0.67;
export const MAX_ZOOM = 1;

function readEnvFlag(name: string): string | undefined {
  try {
    return (import.meta as ImportMeta & { env?: Record<string, string> }).env?.[name];
  } catch {
    return undefined;
  }
}

export function isRootZoomEnabled(): boolean {
  const flag = readEnvFlag('VITE_IPC_ZOOM_ENABLED');
  if (flag == null || flag === '') return true;
  return flag !== 'false' && flag !== '0';
}

/**
 * Apple 平台：iPhone / iPad / iPod，以及 iPadOS 桌面 UA、macOS。
 * 这些环境的 devicePixelRatio 表示 Retina，不是 Windows「显示缩放」，不能 1/dpr。
 */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPod|iPad/i.test(ua)) return true;
  // iPadOS 13+ 常伪装成 Macintosh，用触点区分
  if (/Macintosh/i.test(ua) && Number(navigator.maxTouchPoints || 0) > 1) return true;
  if (/Mac OS X|Macintosh/i.test(ua)) return true;
  return false;
}

/** 环境开关开启，且非 Apple 平台时才做 DPR 抵消 */
export function shouldApplyDpiCompensatingZoom(): boolean {
  return isRootZoomEnabled() && !isApplePlatform();
}

export function getForcedZoom(): number | null {
  const raw = readEnvFlag('VITE_IPC_ZOOM_FORCE');
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

/** 读取用于抵消系统缩放的 DPR（忽略异常值） */
export function readDevicePixelRatio(): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  if (!Number.isFinite(dpr) || dpr <= 0) return 1;
  return dpr;
}

/**
 * 按系统缩放计算 zoom。
 * - 100% → 1
 * - 120% → ≈0.833
 * - 125% → 0.8
 * - 150% → ≈0.667
 */
export function resolveZoom(devicePixelRatio: number = readDevicePixelRatio()): number {
  const forced = getForcedZoom();
  // 调试强制值不夹紧，便于临时验证任意比例
  if (forced != null) return forced;

  const dpr = devicePixelRatio > 0 ? devicePixelRatio : 1;
  if (dpr <= 1) return MAX_ZOOM;

  const raw = 1 / dpr;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, raw));
}

/**
 * 读取当前已应用到 html 的 UI zoom（布局像素 ↔ 可视像素换算用）。
 * 与 useRootZoom 写入的 --ipc-ui-zoom 对齐。
 */
export function readAppliedUiZoom(): number {
  if (typeof document === 'undefined') return 1;
  const raw = document.documentElement.style.getPropertyValue('--ipc-ui-zoom')
    || getComputedStyle(document.documentElement).getPropertyValue('--ipc-ui-zoom');
  const zoom = Number.parseFloat(raw);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}
